import React, {useEffect, useMemo, useState} from "react";
import {AccountingReportRestControllerApiFactory, LayerBalanceRow, LayerOverlap} from "arteco-api-client-ts";
import {
    Box, FormControlLabel, MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableFooter,
    TableHead, TableRow, TextField,
} from "@mui/material";
import {useApiManager} from "../../../../components/utils/api";
import {EmptyState, Page, PageHeader, Panel, SkeletonRows, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {fmtAmt} from "../../../../components/utils/numbers";

/**
 * Cuadre Contasol ↔ Extranet (#14701). Mientras conviven, el mismo hecho se importa de Contasol y se
 * anota en la Extranet: esta pantalla pone lado a lado el saldo de cada cuenta en las dos capas, por
 * mes. Cuando todas las diferencias de varios meses seguidos son cero, la Extranet ya lleva lo mismo
 * que Contasol y puede ser la fuente oficial. Solo se compara el tramo en que conviven las dos capas
 * (desde el primer asiento anotado a mano hasta el último día con asientos en las dos); la cuenta del
 * PGC de 3 dígitos con que se anota aquí se suma a su subcuenta de 8 de Contasol (572 → 57200000), salvo
 * las de terceros, donde la XXX00000 de Contasol es un tercero concreto.
 */
const MESES = ["Todo el tramo", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto",
    "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const NIVELES = [
    {value: 2, label: "Subgrupo (2 dígitos)"},
    {value: 3, label: "Cuenta del PGC (3 dígitos)"},
    {value: 0, label: "Subcuenta"},
];

const isZero = (n?: number) => Math.abs(n ?? 0) < 0.005;
const fmtDia = (iso?: string) => iso ? iso.split("-").reverse().join("-") : "";

export const ContasolCuadrePage = () => {
    const apiManager = useApiManager();
    const thisYear = new Date().getFullYear();
    const [year, setYear] = useState(thisYear);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [digits, setDigits] = useState(3);
    const [onlyDiffs, setOnlyDiffs] = useState(true);
    const [rows, setRows] = useState<LayerBalanceRow[] | null>(null);
    const [overlap, setOverlap] = useState<LayerOverlap | null>(null);

    useEffect(() => {
        setOverlap(null);
        const ctrl = apiManager.factory(AccountingReportRestControllerApiFactory);
        apiManager.execute(ctrl.layerOverlap(year))
            .then(o => setOverlap(o as LayerOverlap))
            .catch(() => setOverlap({}));
    }, [year]);

    useEffect(() => {
        setRows(null);
        const ctrl = apiManager.factory(AccountingReportRestControllerApiFactory);
        apiManager.execute(ctrl.layerBalance(year, month || undefined, digits || undefined))
            .then(list => setRows(list as LayerBalanceRow[]))
            .catch(() => setRows([]));
    }, [year, month, digits]);

    const visible = useMemo(() => (rows ?? []).filter(r => !onlyDiffs || !isZero(r.difference)), [rows, onlyDiffs]);
    const totals = useMemo(() => (rows ?? []).reduce<{ contasol: number, extranet: number, abs: number }>((t, r) => ({
        contasol: t.contasol + (r.contasol ?? 0), extranet: t.extranet + (r.extranet ?? 0),
        abs: t.abs + Math.abs(r.difference ?? 0),
    }), {contasol: 0, extranet: 0, abs: 0}), [rows]);
    const withDiff = (rows ?? []).filter(r => !isZero(r.difference)).length;
    const hasBoth = (rows ?? []).length > 0;

    return (
        <Page>
            <PageHeader
                title="Cuadre Contasol ↔ Extranet"
                subtitle="Saldo de cada cuenta en el diario importado de Contasol y en lo anotado en la Extranet, y la diferencia."
            />
            <Panel>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2}}>
                    <TextField select size="small" label="Ejercicio" value={year} onChange={e => setYear(Number(e.target.value))}
                               sx={{minWidth: 120}}>
                        {[thisYear - 1, thisYear, thisYear + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Mes" value={month} onChange={e => setMonth(Number(e.target.value))}
                               sx={{minWidth: 150}}>
                        {MESES.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Agrupar por" value={digits} onChange={e => setDigits(Number(e.target.value))}
                               sx={{minWidth: 220}}>
                        {NIVELES.map(n => <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>)}
                    </TextField>
                    <FormControlLabel control={<Switch checked={onlyDiffs} onChange={e => setOnlyDiffs(e.target.checked)}/>}
                                      label="Solo diferencias"/>
                    {rows && (hasBoth
                        ? <SoftChip tone={withDiff === 0 ? "success" : "warning"}
                                    label={withDiff === 0 ? "Cuadra" : `${withDiff} con diferencia`}/>
                        : <SoftChip tone="neutral" label="Solo hay una capa en este periodo"/>)}
                </Box>
                {overlap && (
                    <Box sx={{color: "text.secondary", fontSize: 14, mb: 2}}>
                        {overlap.from
                            ? `Las dos capas conviven del ${fmtDia(overlap.from)} al ${fmtDia(overlap.to)}: solo se compara ese tramo. `
                              + "Las cuentas de 3 dígitos de la Extranet se suman a su subcuenta de Contasol (572 → 57200000), salvo las de proveedores, acreedores y clientes (40, 41, 43 y 44)."
                            : "En este ejercicio solo hay una capa: no hay nada que cuadrar."}
                    </Box>
                )}

                {rows === null ? <SkeletonRows rows={6}/> : visible.length === 0 ? (
                    <EmptyState
                        title={rows.length === 0 ? "Sin movimientos en este periodo" : "Sin diferencias"}
                        description={rows.length === 0 ? undefined : "Las dos capas dan el mismo saldo en todas las cuentas."}
                    />
                ) : (
                    <TableContainer sx={tableContainerSx}>
                        <Table size="small" sx={tableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Cuenta</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Contasol</TableCell>
                                    <TableCell align="right">Extranet</TableCell>
                                    <TableCell align="right">Diferencia</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.map(r => (
                                    <TableRow key={r.code}>
                                        <TableCell sx={{fontFamily: "monospace", whiteSpace: "nowrap"}}>{r.code}</TableCell>
                                        <TableCell>{r.name}</TableCell>
                                        <TableCell align="right" sx={{whiteSpace: "nowrap"}}>{fmtAmt(r.contasol)}</TableCell>
                                        <TableCell align="right" sx={{whiteSpace: "nowrap"}}>{fmtAmt(r.extranet)}</TableCell>
                                        <TableCell align="right" sx={{
                                            whiteSpace: "nowrap", fontWeight: isZero(r.difference) ? undefined : 600,
                                            color: isZero(r.difference) ? "text.secondary" : "warning.dark",
                                        }}>{fmtAmt(r.difference)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={2}>Totales (la diferencia, en valor absoluto)</TableCell>
                                    <TableCell align="right">{fmtAmt(totals.contasol)}</TableCell>
                                    <TableCell align="right">{fmtAmt(totals.extranet)}</TableCell>
                                    <TableCell align="right">{fmtAmt(totals.abs)}</TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                )}
            </Panel>
        </Page>
    );
};
